import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import 'bootstrap/dist/css/bootstrap.min.css';

// Cookie Consent
import 'vanilla-cookieconsent/dist/cookieconsent.css';
import * as CookieConsent from 'vanilla-cookieconsent';

// Utils
import ScrollToTop from './components/utils/ScrollToTop';

// Layout Components
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';

// Page Components
import Home from './components/pages/Home';
import About from './components/pages/About';
import Pricing from './components/pages/Pricing';
import Contact from './components/pages/Contact';
import Terms from './components/pages/Terms';
import Privacy from './components/pages/Privacy';

// Platform Pages
import WebsiteAIChatbot from './components/pages/platform/WebsiteAIChatbot';
import WhatsAppIntegration from './components/pages/platform/WhatsAppIntegration';

// Feature Pages
import LeadGeneration from './components/pages/features/LeadGeneration';
import SupportAutomation from './components/pages/features/SupportAutomation';
import AICampaigns from './components/pages/features/AICampaigns';

// Industry Pages
import B2CB2B from './components/pages/industries/B2CB2B';
import Hospitality from './components/pages/industries/Hospitality';
import Education from './components/pages/industries/Education';

// Demo Page
import Demo from './components/pages/demo/Demo';

import { useState } from 'react';
import LogoLoader from './LogoLoader'; // update path if needed

function App() {
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000); // show loader for 2 seconds (you can adjust)

    return () => clearTimeout(timer);
  }, []);
  useEffect(() => {
    // Scroll animation on fade-in elements
    const handleScroll = () => {
      const elements = document.querySelectorAll('.fade-in');
      elements.forEach((element) => {
        const position = element.getBoundingClientRect();
        if (position.top < window.innerHeight && position.bottom >= 0) {
          element.classList.add('visible');
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    CookieConsent.run({
      cookie: {
        name: 'cc_cookie',
        sameSite: 'Lax',
        expiresAfterDays: 182,
      },
      guiOptions: {
        consentModal: {
          layout: 'box',
          position: 'bottom left',
          equalWeightButtons: true,
          flipButtons: false,
        },
        preferencesModal: {
          layout: 'box',
          equalWeightButtons: true,
          flipButtons: false,
        },
      },
      onFirstConsent: ({ cookie }) => {
        // console.log("User gave first consent:", cookie);
      },
      onConsent: ({ cookie }) => {
        // console.log("User updated consent:", cookie);
      },
      onChange: ({ changedCategories, changedServices }) => {
        // console.log("Consent settings changed:", changedCategories, changedServices);
      },
      categories: {
        necessary: {
          enabled: true,
          readOnly: true,
        },
        analytics: {
          autoClear: {
            cookies: [{ name: /^_ga/ }, { name: '_gid' }],
          },
          services: {
            ga: {
              label: 'Google Analytics',
              onAccept: () => {
                // console.log('Google Analytics cookies accepted.');
                // Dynamically load GA script after consent
                gtag('consent', 'update', {
                  ad_user_data: 'granted',
                  ad_personalization: 'granted',
                  ad_storage: 'granted',
                  analytics_storage: 'granted',
                });
              },
              onReject: () => console.log('Google Analytics cookies rejected.'),
            },
          },
        },
        ads: {
          label: 'Advertising',
        },
      },
      language: {
        default: 'en',
        translations: {
          en: {
            consentModal: {
              title: 'We Value Your Privacy',
              description:
                'We use cookies to enhance your browsing experience, analyze website traffic, and serve personalized content and advertisements. You can manage your preferences or accept all cookies.',
              acceptAllBtn: 'Accept All Cookies',
              acceptNecessaryBtn: 'Reject Non-Essential Cookies',
              showPreferencesBtn: 'Customize Settings',
              footer: `<a href="/terms">Privacy Policy</a>`,
            },
            preferencesModal: {
              title: 'Manage Cookie Preferences',
              acceptAllBtn: 'Accept All Cookies',
              acceptNecessaryBtn: 'Reject Non-Essential Cookies',
              savePreferencesBtn: 'Save Preferences',
              closeIconLabel: 'Close Settings',
              serviceCounterLabel: 'Service|Services',
              sections: [
                {
                  title: 'Your Privacy Choices',
                  description:
                    "You can review and manage your cookie preferences below. You may change your preferences at any time. To proceed, use the toggles below or click 'Accept All Cookies' to grant full consent.",
                },
                {
                  title: 'Strictly Necessary Cookies',
                  description:
                    'These cookies are essential for the website to function properly and cannot be disabled.',
                  linkedCategory: 'necessary',
                },
                {
                  title: 'Performance and Analytics',
                  description:
                    'These cookies help us understand how visitors interact with our site and improve it.',
                  linkedCategory: 'analytics',
                  cookieTable: {
                    caption: 'List of Analytics Cookies',
                    headers: {
                      name: 'Cookie Name',
                      domain: 'Domain',
                      desc: 'Description',
                    },
                    body: [
                      {
                        name: '_ga',
                        domain: location.hostname,
                        desc: 'Used by Google Analytics to distinguish users.',
                      },
                      {
                        name: '_gid',
                        domain: location.hostname,
                        desc: 'Used by Google Analytics to analyze user behavior.',
                      },
                    ],
                  },
                },
                {
                  title: 'Advertising and Targeting Cookies',
                  description:
                    'These cookies are used to deliver relevant ads and measure marketing effectiveness.',
                  linkedCategory: 'ads',
                },
                {
                  title: 'More Information',
                  description:
                    "For more details, please visit our <a href='/contact' target='_blank'>Contact</a> page.",
                },
              ],
            },
          },
        },
      },
    });
  }, []);

  return (
    <>
      {isLoading ? (
        <LogoLoader />
      ) : (
        <div className="App">
          <ScrollToTop />
          <Header />
          <main>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />

              {/* Platform */}
              <Route path="/platform/website-ai-chatbot" element={<WebsiteAIChatbot />} />
              <Route path="/platform/whatsapp-integration" element={<WhatsAppIntegration />} />

              {/* Features */}
              <Route path="/features/lead-generation" element={<LeadGeneration />} />
              <Route path="/features/support-automation" element={<SupportAutomation />} />
              <Route path="/features/ai-campaigns" element={<AICampaigns />} />

              {/* Industries */}
              <Route path="/industries/b2c-d2c" element={<B2CB2B />} />
              <Route path="/industries/hospitality" element={<Hospitality />} />
              <Route path="/industries/education" element={<Education />} />

              {/* Demo */}
              <Route path="/demo" element={<Demo />} />

              {/* Redirect unmatched routes */}
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </main>
          <Footer />
        </div>
      )}
    </>
  );
}

export default App;
